import { TStringField } from '../../../types/sitecore.types';
import { THeadingTag } from '../../../types/types';
import { isEmptyObject } from '../../../utils/checkEmptyObject';
import { TSocialMediaBarProps } from '../../@shared/socialMediaBar/types/socialMediaBar.types';
import {
  TAddressDetails,
  TAwardProps,
  TBusinessHoursMappedValue,
  TDealerDetailProps,
  TDealerLocatorDetailFilterDetails,
  TDealerLocatorDetailProps,
  TDealerLocatorPropsFields,
  Weekdays,
} from '../types/dealerLocatorDetail.types';

export class DealerLocatorModel {
  private readonly fields?: TDealerLocatorPropsFields;
  private readonly context?: any;
  private readonly itemData?: any;
  private readonly params?: any;
  private readonly dealerDetails?: TDealerDetailProps;

  constructor(props: TDealerLocatorDetailProps) {
    this.fields = props.fields || undefined;
    this.dealerDetails = this.fields?.apiData?.dealerDetail;
    this.params = props.params;
    this.context = props?.sitecoreContext;
    this.itemData = props?.fields?.itemData;
  }

  get headingTag(): THeadingTag {
    return (this.params?.headingTag?.toLowerCase() as THeadingTag) || 'h2';
  }

  get dealerLocatorGoogleMapsAPIKey(): string {
    return this?.itemData?.fields?.settings?.dealerLocatorGoogleMapsAPIKey;
  }

  get latitude(): string {
    return this.dealerDetails?.latitude ?? '';
  }

  get longitude(): string {
    return this.dealerDetails?.longitude ?? '';
  }

  get addressDetails(): TAddressDetails {
    return {
      fax: this.dealerDetails?.shippingFax,
      phone: this.dealerDetails?.shippingPhone,
      email: this.dealerDetails?.dealerEmail,
      website: this.dealerDetails?.dealershipAttributes?.website,
      address: `${this.dealerDetails?.shippingAddress1}, ${this.dealerDetails?.shippingCity}, ${this.dealerDetails?.shippingStateProv || ''} ${this.dealerDetails?.shippingZip}`,
    };
  }
  get businessHours(): TBusinessHoursMappedValue {
    const dealerAttributes = this.dealerDetails?.dealershipAttributes;
    const hoursInDay = ['AMStart', 'AMEnd', 'PMStart', 'PMEnd'];
    const hours: TBusinessHoursMappedValue = {};

    if (dealerAttributes) {
      for (const day in Weekdays) {
        let morningHour = '';
        if (dealerAttributes[day + hoursInDay[0]]) {
          morningHour = morningHour + dealerAttributes[day + hoursInDay[0]];
        }
        morningHour = morningHour + ' - ';
        if (dealerAttributes[day + hoursInDay[1]]) {
          morningHour = morningHour + dealerAttributes[day + hoursInDay[1]];
        }

        let eveningHour = '';
        if (dealerAttributes[day + hoursInDay[2]]) {
          eveningHour = eveningHour + dealerAttributes[day + hoursInDay[2]];
        }
        eveningHour = eveningHour + ' - ';
        if (dealerAttributes[day + hoursInDay[3]]) {
          eveningHour = eveningHour + dealerAttributes[day + hoursInDay[3]];
        }

        hours[day] = [morningHour.trim(), eveningHour.trim()];
      }
    }
    return hours;
  }

  get hasBusinessHours(): boolean {
    const nonEmptyValue = Object.values(this.businessHours).find((hours) => hours[0] !== '-' || hours[1] !== '-');
    return !!nonEmptyValue;
  }

  get siteLanguage(): string {
    return this.context?.language || '';
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get dealerExists(): boolean {
    return !!this.dealerDetails?.dealerName;
  }

  get dealerName(): TStringField {
    return { value: this.dealerDetails?.dealerName };
  }

  get descriptionExists(): boolean {
    return !!this.dealerDetails?.dealershipAttributes?.message;
  }

  get description(): TStringField {
    const dealerAttributes = this.dealerDetails?.dealershipAttributes;
    return { value: dealerAttributes?.message };
  }

  get socialMediaBarProps(): TSocialMediaBarProps | undefined {
    return isEmptyObject(this.fields?.socialMediaBar) ? undefined : this.fields?.socialMediaBar;
  }

  get dealerImage(): string | undefined {
    const dealerAttributes = this.dealerDetails?.dealershipAttributes;
    const photoImage = dealerAttributes?.photoImage;
    return photoImage;
  }

  get logoImage(): string | undefined {
    const dealerAttributes = this.dealerDetails?.dealershipAttributes;
    const logoImage = dealerAttributes?.logoImage;
    return logoImage;
  }

  get mediaExists(): boolean {
    return !!(this.dealerImage ?? this.logoImage);
  }

  get promotionsExist(): boolean {
    return !!this.dealerDetails?.dealershipAttributes?.promo;
  }

  get promotions(): TStringField {
    const dealerAttributes = this.dealerDetails?.dealershipAttributes;
    return { value: dealerAttributes?.promo };
  }

  get certifications(): TAwardProps[] {
    const dealerAttributes = this.dealerDetails?.dealershipAttributes;
    const awardDesignations = dealerAttributes?.awardDesignations;
    return awardDesignations ?? [];
  }

  get hasCertifications(): boolean {
    return this.certifications.length > 0;
  }

  get businessHoursAndCertificationsSectionExists(): boolean {
    return this.hasBusinessHours || this.hasCertifications;
  }

  get equipments(): TDealerLocatorDetailFilterDetails[] {
    return this.itemData?.fields?.filters?.Equipments ?? [];
  }

  get services(): TDealerLocatorDetailFilterDetails[] {
    return this.itemData?.fields?.filters?.Services ?? [];
  }
}
